<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { Combobox, ComboboxInput, ComboboxButton, ComboboxOptions, ComboboxOption } from '@headlessui/vue'
import IconSelect from '../Icon/IconSelect.vue'
import ShowImage from '../Media/ShowImage.vue'
import { useEventWefoValidation } from '../../composables/useEventWefoValidation'
import { useEventStore } from '@/store/event'

interface IconOption {
  id: number
  name: string
  image_url: string | null
}

interface SelectedIcon {
  id: number
  name: string
  image_url: string | null
  description: string
  sort_order: number
}

const props = defineProps<{
  locations: any[]
  allIcons: IconOption[]
  eventId?: number | null
}>()

const store = useEventStore()

// Local reactive data derived from store for validation binding
const validationData = computed(() => ({
  tile_title: store.wefo_data?.tile_title ?? '',
  title1: store.wefo_data?.title1 ?? '',
  title2: store.wefo_data?.title2 ?? '',
  description: store.wefo_data?.description ?? '',
  walking_time: store.wefo_data?.walking_time ?? null,
}))

const enabled = computed(() => store.wefo_enabled)

// Validation
const { v$ } = useEventWefoValidation(validationData, enabled)
defineExpose({ v$ })

// Initialize wefo_data if null
if (!store.wefo_data) {
  store.wefo_data = {
    tile_title: '',
    title1: '',
    title2: '',
    description: '',
    walking_time: null,
  }
}

const updateField = (field: string, value: any) => {
  store.wefo_data = { ...store.wefo_data, [field]: value }
}

// Location combobox
const queryLocation = ref('')

const filteredLocations = computed(() => {
  if (queryLocation.value === '') return props.locations
  return props.locations.filter((loc: any) =>
    (loc.title ?? '').toLowerCase().includes(queryLocation.value.toLowerCase())
  )
})

const selectedLocationTitle = computed(() => {
  if (!store.wefo_location_id) return ''
  const loc = props.locations.find((l: any) => l.id === store.wefo_location_id)
  return loc?.title ?? ''
})

const onIconsChanged = (icons: SelectedIcon[]) => {
  store.wefo_icons = icons
}

const getError = (field: string): string | null => {
  const vField = (v$.value as any)[field]
  if (vField?.$errors?.length) {
    return vField.$errors[0].$message
  }
  return null
}

const hasError = (field: string): boolean => {
  return !!getError(field)
}

const wefoImageChannels = [
  { channel: 'wefo-1', label: 'Bild 1', dimensions: '432 × 528 px', width: 432, height: 528 },
  { channel: 'wefo-2', label: 'Bild 2', dimensions: '396 × 438 px', width: 396, height: 438 },
  { channel: 'wefo-3', label: 'Bild 3', dimensions: '545 × 438 px', width: 545, height: 438 },
  { channel: 'wefo-4', label: 'Bild 4', dimensions: '1078 × 1080 px', width: 1078, height: 1080 },
]
</script>

<template>
  <div class="card w-full bg-base-100 mt-4">
    <div class="card-body">
      <h2 class="card-title">Welterbe-Forum</h2>

      <!-- WeFo Enable Toggle -->
      <div class="form-control mb-4">
        <label class="label cursor-pointer justify-start">
          <input
            type="checkbox"
            class="toggle toggle-primary mr-2"
            v-model="store.wefo_enabled"
            :true-value="true"
            :false-value="false"
          />
          <span class="label-text font-medium">Im Welterbe-Forum anzeigen</span>
        </label>
      </div>

      <!-- WeFo Fields (conditional) -->
      <div v-show="store.wefo_enabled">
        <div class="divider"></div>

        <h6 class="text-md font-semibold mb-3">Kachel & Titel</h6>
        <div class="flex flex-wrap gap-x-4">
          <label class="form-control w-full md:w-[calc(50%-0.5rem)]">
            <div class="label"><span class="label-text">Kachel-Titel</span></div>
            <input
              type="text"
              :value="store.wefo_data?.tile_title ?? ''"
              @input="updateField('tile_title', ($event.target as HTMLInputElement).value)"
              @blur="v$.tile_title?.$touch()"
              class="input input-bordered input-sm w-full"
              :class="{ 'input-error': hasError('tile_title') }"
              maxlength="255"
              placeholder="z.B. Zollverein Eisbahn"
            />
            <div v-if="getError('tile_title')" class="label"><span class="label-text-alt text-error">{{ getError('tile_title') }}</span></div>
          </label>
          <label class="form-control w-full md:w-[calc(50%-0.5rem)]">
            <div class="label"><span class="label-text">Titel 1</span></div>
            <input
              type="text"
              :value="store.wefo_data?.title1 ?? ''"
              @input="updateField('title1', ($event.target as HTMLInputElement).value)"
              @blur="v$.title1?.$touch()"
              class="input input-bordered input-sm w-full"
              :class="{ 'input-error': hasError('title1') }"
              maxlength="255"
              placeholder="z.B. Eisbahn"
            />
            <div v-if="getError('title1')" class="label"><span class="label-text-alt text-error">{{ getError('title1') }}</span></div>
          </label>
          <label class="form-control w-full md:w-[calc(50%-0.5rem)]">
            <div class="label"><span class="label-text">Titel 2</span></div>
            <input
              type="text"
              :value="store.wefo_data?.title2 ?? ''"
              @input="updateField('title2', ($event.target as HTMLInputElement).value)"
              @blur="v$.title2?.$touch()"
              class="input input-bordered input-sm w-full"
              :class="{ 'input-error': hasError('title2') }"
              maxlength="255"
              placeholder="z.B. auf dem Welterbe"
            />
            <div v-if="getError('title2')" class="label"><span class="label-text-alt text-error">{{ getError('title2') }}</span></div>
          </label>
        </div>

        <div class="divider"></div>

        <h6 class="text-md font-semibold mb-3">Beschreibung & Ort</h6>
        <label class="form-control w-full mb-2">
          <div class="label"><span class="label-text">Beschreibung</span></div>
          <textarea
            :value="store.wefo_data?.description ?? ''"
            @input="updateField('description', ($event.target as HTMLTextAreaElement).value)"
            @blur="v$.description?.$touch()"
            class="textarea textarea-bordered w-full"
            :class="{ 'textarea-error': hasError('description') }"
            rows="4"
            maxlength="5000"
            placeholder="Beschreibungstext..."
          ></textarea>
          <div v-if="getError('description')" class="label"><span class="label-text-alt text-error">{{ getError('description') }}</span></div>
        </label>

        <div class="flex flex-wrap gap-x-4">
          <!-- WeFo Location-->
          <div class="form-control w-full md:w-[calc(50%-0.5rem)]">
            <div class="label"><span class="label-text">Ort</span></div>
            <div class="relative">
              <Combobox v-model="store.wefo_location_id" nullable>
                <div class="relative">
                  <ComboboxInput
                    class="input input-bordered input-sm w-full"
                    :display-value="() => selectedLocationTitle"
                    placeholder="Location suchen..."
                    @change="queryLocation = $event.target.value"
                  />
                  <ComboboxButton class="absolute inset-y-0 right-0 flex items-center pr-2">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z" /></svg>
                  </ComboboxButton>
                </div>
                <ComboboxOptions class="menu bg-base-100 rounded-box absolute overflow-auto w-full z-10 shadow-lg max-h-60">
                  <ComboboxOption :value="null" class="relative">
                    <button class="text-gray-400 italic">— Keine Auswahl —</button>
                  </ComboboxOption>
                  <ComboboxOption v-for="location in filteredLocations" :key="location.id" :value="location.id" class="relative">
                    <button :class="{ 'active': store.wefo_location_id === location.id }">{{ location.title }}</button>
                  </ComboboxOption>
                </ComboboxOptions>
              </Combobox>
            </div>
          </div>

          <!-- Walking Time -->
          <label class="form-control w-full md:w-[calc(50%-0.5rem)]">
            <div class="label"><span class="label-text">Fußweg (Minuten)</span></div>
            <input
              type="number"
              :value="store.wefo_data?.walking_time ?? ''"
              @input="updateField('walking_time', ($event.target as HTMLInputElement).value ? Number(($event.target as HTMLInputElement).value) : null)"
              @blur="v$.walking_time?.$touch()"
              class="input input-bordered input-sm w-full"
              :class="{ 'input-error': hasError('walking_time') }"
              min="1"
              max="999"
              placeholder="z.B. 12"
            />
            <div v-if="getError('walking_time')" class="label"><span class="label-text-alt text-error">{{ getError('walking_time') }}</span></div>
          </label>
        </div>

        <div class="divider"></div>

        <h6 class="text-md font-semibold mb-3">Icons für Barrierefreiheit</h6>
        <icon-select
          :all-icons="allIcons"
          :selected-icons="store.wefo_icons"
          @update:model-value="onIconsChanged"
        />

        <!-- WeFo Images (only for existing events) -->
        <template v-if="eventId">
          <div class="divider"></div>
          <h6 class="text-md font-semibold mb-3">Bilder</h6>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div v-for="img in wefoImageChannels" :key="img.channel">
              <div class="text-sm font-medium mb-1">{{ img.label }} <span class="text-gray-400">({{ img.dimensions }})</span></div>
              <ShowImage :parent="{ id: eventId, type: 'event', channel: img.channel }" :required-width="img.width" :required-height="img.height" />
            </div>
          </div>
        </template>
        <div v-else class="text-sm text-gray-500 italic mt-4">
          Bilder können nach dem Erstellen des Events hochgeladen werden.
        </div>
      </div>
    </div>
  </div>
</template>
